import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { GiPlantRoots } from 'react-icons/gi';
import { farmerAPI, statsAPI, productAPI } from '../../api/api';
import { useApi } from '../../api/useApi';
import { scrollToHomeSection } from '../../utils/scrollToHomeSection';
import homebg from '../images/sedyam_header.jpg';

const fallbackBackground = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1800";

export default function HeroSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: statsData } = useApi(statsAPI.getGlobal, { summary: 'true' }, { staleTime: 60000 });
  const { data: productsData } = useApi(productAPI.getAll, null, { staleTime: 45000 });
  const { data: farmersData } = useApi(farmerAPI.getPublic, null, { staleTime: 45000 });

  const featuredProduct = useMemo(() => {
    const products = Array.isArray(productsData) ? productsData : [];
    return products.find((product) => Number(product?.availableQuantityKg || 0) > 0) || products[0] || null;
  }, [productsData]);

  const { verifiedFarmersCount, liveProductsCount, tertiaryMetric } = useMemo(() => {
    const stats = statsData || {};
    const publicFarmers = Array.isArray(farmersData) ? farmersData : [];
    const products = Array.isArray(productsData) ? productsData : [];
    const inStockProducts = products.filter((product) => Number(product?.availableQuantityKg || 0) > 0);

    const verifiedFarmersCount = Number(stats.verifiedFarmers ?? publicFarmers.length ?? 0);
    const liveProductsCount = Number(stats.availableProducts ?? inStockProducts.length ?? products.length ?? 0);

    if (Number.isFinite(Number(stats.totalCustomers)) && Number(stats.totalCustomers) > 0) {
      return {
        verifiedFarmersCount,
        liveProductsCount,
        tertiaryMetric: { value: `${Number(stats.totalCustomers)}+`, label: 'Customers' },
      };
    }

    if (Number.isFinite(Number(stats.totalOrders)) && Number(stats.totalOrders) > 0) {
      return {
        verifiedFarmersCount,
        liveProductsCount,
        tertiaryMetric: { value: `${Number(stats.totalOrders)}+`, label: 'Orders' },
      };
    }

    return {
      verifiedFarmersCount,
      liveProductsCount,
      tertiaryMetric: { value: `${publicFarmers.length}+`, label: 'Farm Network' },
    };
  }, [farmersData, productsData, statsData]);

  const liveStats = useMemo(() => {
    return [
      { value: `${verifiedFarmersCount}+`, label: 'Verified Farmers' },
      { value: `${liveProductsCount}+`, label: 'Live Products' },
      tertiaryMetric,
    ];
  }, [liveProductsCount, tertiaryMetric, verifiedFarmersCount]);

  const openSection = (sectionId) => (event) => {
    event.preventDefault();

    if (location.pathname === '/') {
      scrollToHomeSection(sectionId);
      return;
    }

    navigate('/', { state: { scrollToSection: sectionId } });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url("${homebg || fallbackBackground}")` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60" />

      <div className="hidden md:block absolute top-32 left-8 md:left-20 bg-white/90 rounded-2xl px-4 py-3 shadow-lg animate-bounce z-10">
        <p className="text-xs text-gray-500">Today's Pick</p>
        <p className="font-bold text-primary text-sm">
          {featuredProduct ? `${featuredProduct.name} - Rs.${featuredProduct.pricePerKg}/kg` : 'Fresh produce loading...'}
        </p>
      </div>

      <div className="absolute bottom-24 right-4 sm:bottom-32 sm:right-8 md:right-20 bg-white/90 rounded-2xl px-3 py-2 md:px-4 md:py-3 shadow-lg z-10">
        <p className="text-xs text-gray-500">Trusted Farmers</p>
        <p className="font-bold text-primary text-sm">{verifiedFarmersCount}+ Verified</p>
      </div>

      <div className="relative z-10 text-center text-white px-4 sm:px-6 max-w-4xl mx-auto">
        <div className="md:hidden mx-auto mb-4 w-fit bg-white/90 rounded-xl px-3 py-2 shadow-lg">
          <p className="text-[11px] text-gray-500 text-left">Today's Pick</p>
          <p className="font-bold text-primary text-xs sm:text-sm">
            {featuredProduct ? `${featuredProduct.name} - Rs.${featuredProduct.pricePerKg}/kg` : 'Fresh produce loading...'}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-5 sm:mb-6">
          <GiPlantRoots className="text-secondary text-2xl sm:text-3xl" />
          <span className="bg-secondary/20 border border-secondary text-secondary px-3 sm:px-4 py-1 rounded-full text-sm font-medium">
            Verified Farmers • Fresh Produce • Fair Pricing
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold leading-tight mb-5 sm:mb-6">
          Fresh From Farmers
          <br />
          <span className="text-secondary">Fair for Everyone</span>
        </h1>

        <p className="text-lg md:text-xl text-gray-200 mb-8 sm:mb-10 max-w-2xl mx-auto">
          Zero Adulteration. No markups. Buy directly from verified Indian farmers and taste the real difference.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#products" onClick={openSection('products')} className="bg-primary hover:bg-green-800 text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2 transition text-lg">
            Shop Now <FiArrowRight />
          </a>
          <a href="#farmers" onClick={openSection('farmers')} className="bg-white/20 hover:bg-white/30 border border-white text-white px-8 py-4 rounded-full font-semibold transition text-lg">
            Meet Our Farmers
          </a>
        </div>

        <div className="mt-12 sm:mt-16 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg mx-auto">
          {liveStats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-secondary">{stat.value}</p>
              <p className="text-gray-300 text-xs sm:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
